import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { TrendingUp, Target, Percent, Boxes } from "lucide-react";
import { PageHeader, Panel, StatCard } from "@/components/dashboard/primitives";
import { fmtUSD } from "@/components/common";
import { analyticsCategory, analyticsTrend } from "@/data/demoData";

const COLORS = ["#4F8CFF", "#7C5CFF", "#10B981", "#F59E0B"];

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-[#0B1526] px-3 py-2 text-xs">
      <p className="text-slate-400">{label || payload[0].name}</p>
      <p className="text-white font-semibold">{fmtUSD(payload[0].value)}</p>
    </div>
  );
}

export default function Analytics() {
  const total = analyticsCategory.reduce((s, c) => s + c.value, 0);

  return (
    <div>
      <PageHeader title="Analytics" subtitle="Agent spend analytics and unit economics." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} label="Spend this month" value={fmtUSD(2184)} />
        <StatCard icon={Target} label="Avg cost / task" value={fmtUSD(0.42)} accent="text-[#4F8CFF]" />
        <StatCard icon={Percent} label="Budget utilisation" value="68%" />
        <StatCard icon={Boxes} label="Merchants" value="14" />
      </div>

      <div className="mt-5 grid lg:grid-cols-3 gap-5">
        <Panel className="lg:col-span-2 p-6" data-testid="analytics-trend">
          <h3 className="text-base font-semibold text-white mb-5">Monthly spend trend</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analyticsTrend} margin={{ left: -18, right: 6, top: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="month" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTip />} cursor={{ stroke: "rgba(79,140,255,0.3)" }} />
                <Line type="monotone" dataKey="spend" stroke="#4F8CFF" strokeWidth={2.5} dot={{ fill: "#4F8CFF", r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel className="p-6" data-testid="analytics-category">
          <h3 className="text-base font-semibold text-white mb-5">By category</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={analyticsCategory} dataKey="value" nameKey="name" innerRadius={48} outerRadius={72} paddingAngle={3} stroke="none">
                  {analyticsCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<ChartTip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2">
            {analyticsCategory.map((c, i) => (
              <div key={c.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-300">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                  {c.name}
                </span>
                <span className="text-slate-400">{((c.value / total) * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel className="mt-5 p-6" data-testid="analytics-merchant-bar">
        <h3 className="text-base font-semibold text-white mb-5">Spend by category (absolute)</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={analyticsCategory} margin={{ left: -18, right: 6, top: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="name" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip content={<ChartTip />} cursor={{ fill: "rgba(79,140,255,0.08)" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {analyticsCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
    </div>
  );
}
